import qs from 'qs';
import { updateAppointmentsDetailsInWindowDataLayer } from '~/analytics/analytics.utils';
import { serviceApi } from '../service';
import {
  appointmentDraftApi,
  getReduxDraftFromDecodedToken,
  getApiDraftFromReduxDraft,
} from './appointment-draft';
import {
  getTokenFromStorage,
  setTokenToStorage,
} from '~/components/persist-token/persist-token.utils';

const actionPrefix = (actionType) => `AppointmentDraft/${actionType}`;

export const SET_DRAFT_APPOINTMENT = actionPrefix('SET_DRAFT_APPOINTMENT');
const setDraftAppointment = (draft) => ({
  type: SET_DRAFT_APPOINTMENT,
  payload: draft,
});

const updateJLDataLayer = (decodedToken, config) => {
  const { appointmentType, store } = decodedToken;
  if (config.getAppointmentsDetails) {
    const appointmentsDetails = config.getAppointmentsDetails(appointmentType);
    if (store && store.id) {
      appointmentsDetails.branchId = store.id;
    }
    updateAppointmentsDetailsInWindowDataLayer(appointmentsDetails);
  }
};

const restoreSelectedData = (token) => (dispatch, getState) => {
  const {
    services: { config },
  } = getState();
  return appointmentDraftApi
    .getDecodedAppointmentDraft(token)
    .then((decodedToken) => {
      const reduxDraft = getReduxDraftFromDecodedToken(decodedToken);
      setTokenToStorage(token);
      dispatch(setDraftAppointment(reduxDraft));
      if (decodedToken.appointmentType) {
        updateJLDataLayer(decodedToken, config);
      }
    })
    .then(
      () => Promise.resolve(),
      (e) => {
        console.error(e);
      },
    );
};

export const SET_APPOINTMENT_TYPE = actionPrefix('SET_APPOINTMENT_TYPE');
const setAppointmentType = (appointmentType) => ({
  type: SET_APPOINTMENT_TYPE,
  payload: appointmentType,
});

export const UPDATE_APPOINTMENT_TYPE = actionPrefix('UPDATE_APPOINTMENT_TYPE');
const updateDraftAppointmentType = (appointmentType) => ({
  type: UPDATE_APPOINTMENT_TYPE,
  payload: appointmentType,
});

export const SET_SHOP = actionPrefix('SET_SHOP');
const setShop = (shop) => ({ type: SET_SHOP, payload: shop });

export const SET_PARTNER = actionPrefix('SET_PARTNER');
const setPartner = (partner) => ({ type: SET_PARTNER, payload: partner });

export const SET_DATE_SLOT = actionPrefix('SET_DATE_SLOT');
const setDateSlot = (dateSlot) => ({ type: SET_DATE_SLOT, payload: dateSlot });

export const SET_TIME_SLOT = actionPrefix('SET_TIME_SLOT');
const setTimeSlot = (selectedTime) => ({ type: SET_TIME_SLOT, payload: selectedTime });

export const SET_RELATED_APPOINTMENT_TIME_SLOT = actionPrefix('SET_RELATED_APPOINTMENT_TIME_SLOT');
const setRelatedAppointmentTimeSlot = (selectedTime) => ({
  type: SET_RELATED_APPOINTMENT_TIME_SLOT,
  payload: selectedTime,
});

export const SET_ADDRESS = actionPrefix('SET_ADDRESS');
const setAddress = (address) => ({ type: SET_ADDRESS, payload: address });

export const SET_EMPTY_ADDRESS = actionPrefix('SET_EMPTY_ADDRESS');
const setEmptyAddress = () => ({ type: SET_EMPTY_ADDRESS });

export const SET_ADDRESS_DESCRIPTION = actionPrefix('SET_ADDRESS_DESCRIPTION');
const setAddressDescription = (addressDescription) => ({
  type: SET_ADDRESS_DESCRIPTION,
  payload: addressDescription,
});

export const SET_EMPTY_ADDRESS_DESCRIPTION = actionPrefix('SET_EMPTY_ADDRESS_DESCRIPTION');
const setEmptyAddressDescription = () => ({ type: SET_EMPTY_ADDRESS_DESCRIPTION });

export const SET_PARTNERS_GROUP_ID = actionPrefix('SET_PARTNERS_GROUP_ID');
const setPartnersGroupId = (partnersGroupId) => ({
  type: SET_PARTNERS_GROUP_ID,
  payload: partnersGroupId,
});

export const SET_RELATED_APPOINTMENT_DRAFT = actionPrefix('SET_RELATED_APPOINTMENT_DRAFT');
const setRelatedAppointmentDraft = (appointmentType) => ({
  type: SET_RELATED_APPOINTMENT_DRAFT,
  payload: appointmentType,
});

export const SET_PRODUCTS = actionPrefix('SET_PRODUCTS');
const setProducts = (products) => ({ type: SET_PRODUCTS, payload: products });

export const SET_FITTING_TYPES = actionPrefix('SET_FITTING_TYPES');
const setFittingTypes = (fittingTypes) => ({ type: SET_FITTING_TYPES, payload: fittingTypes });
export const SET_PIERCING_TYPE = actionPrefix('SET_PIERCING_TYPE');
const setPiercingType = (piercingType) => ({ type: SET_PIERCING_TYPE, payload: piercingType });
export const SELECTED_SERVICE_TYPE_CHANGED = actionPrefix('SELECTED_SERVICE_TYPE_CHANGED');
const setSelectedServiceTypeChanged = (isChanged) => ({
  type: SELECTED_SERVICE_TYPE_CHANGED,
  payload: isChanged,
});

const saveAppointmentDraft =
  (overrides = {}) =>
  (dispatch, getState) => {
    const {
      appointment: { draft },
    } = getState();
    const apiDraft = getApiDraftFromReduxDraft({ ...draft, ...overrides });

    return appointmentDraftApi
      .updateAppointmentDraft({ ...apiDraft, token: getTokenFromStorage() })
      .then(({ token }) => {
        setTokenToStorage(token);
      })
      .then(() => Promise.resolve(), console.error);
  };

const updateAppointmentType = (updatedAppointmentTypeId) => (dispatch, getState) => {
  const {
    appointment: {
      draft: {
        shop: { id: shopId },
        appointmentType: { id: appointmentTypeId },
      },
    },
  } = getState();

  const id = updatedAppointmentTypeId || appointmentTypeId;

  if (id) {
    return serviceApi
      .fetchJSON(`appointment-types/${id}${qs.stringify({ shopId }, { addQueryPrefix: true })}`)
      .then((appointmentTypeResponse) => {
        dispatch(updateDraftAppointmentType(appointmentTypeResponse));
        return Promise.resolve(appointmentTypeResponse);
      });
  }
  return Promise.resolve();
};

const resetAppointmentType = () => (dispatch) => {
  dispatch(updateDraftAppointmentType({ id: '' }));
  return Promise.resolve();
};

const saveDraftAppointmentType = (appointmentTypeId) => (dispatch, getState) => {
  const {
    appointment: {
      draft: {
        shop: { id: shopId },
      },
    },
  } = getState();

  return serviceApi
    .fetchJSON(
      `appointment-types/${appointmentTypeId}${qs.stringify({ shopId }, { addQueryPrefix: true })}`,
    )
    .then((appointmentTypeResponse) => {
      dispatch(updateDraftAppointmentType(appointmentTypeResponse));
      return dispatch(saveAppointmentDraft());
    });
};

export const appointmentDraftActions = {
  setDraftAppointment,
  restoreSelectedData,
  updateAppointmentType,
  updateDraftAppointmentType,

  setAppointmentType,
  setShop,
  setPartner,
  setDateSlot,
  setTimeSlot,
  setRelatedAppointmentTimeSlot,
  setAddress,
  setEmptyAddress,
  setAddressDescription,
  setEmptyAddressDescription,
  setPartnersGroupId,
  setRelatedAppointmentDraft,
  setProducts,
  setFittingTypes,
  setPiercingType,
  saveAppointmentDraft,
  saveDraftAppointmentType,
  setSelectedServiceTypeChanged,
  resetAppointmentType,
};
